import * as mobx from 'mobx';
import { action, observable } from 'mobx';
import * as IoC from 'typescript-ioc';
import {
    CustomFieldsOptionsListDTO,
    CustomPageConfigurationType,
    CustomPageType,
    ICustomPageConfigurationDTO,
    ICustomPagesDAO,
    IMeetingPresenterGroupDTO,
    IRestrictFilterProps,
} from '../../../../../../../api/custom-pages.dao';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import {
    ENABLED,
    LABEL,
    LINK_DESCRIPTION,
    PAGE_LINK_ENABLED,
    PATH,
    PRESENTER_GROUPS,
    PRESENTER_GROUPS_ENABLED,
    PRESENTER_GROUPS_FILTERS,
    pagePathValidatorComposer
} from '../../../../../../../modules/passport-pages/config';
import { CustomPagesCollection } from '../../../../../../../modules/passport-pages/custom-pages/custom-page-collection';
import { CustomPageMapper } from '../../../../../../../modules/passport-pages/custom-pages/custom-page.mapper';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { CustomPageEntity } from '../../../../../../../modules/passport-pages/custom-pages/custom-page.entity';
import { PassportPageSubWindowService } from '../passport-page.sub-window';
import { CustomPageEntityConfig } from '../../../../../../../modules/passport-pages/custom-pages/custom-page-entity.config';
import { FormVmItemConfigType } from '../../../../../../../infrastructure/form-viewmodel-controller';
import { IJobDescriptionDTO, IUploadCustomPageZipJobResult, IUploadThemeArchiveJobDetails, JobStatus, JobType, } from '../../../../../../../api/jobs.dao';
import { IJobsMonitor } from '../../../../../../../domain/jobs-monitor/jobs-monitor';
import { CustomPageFormViewModel } from './custom-page.viewmodel';
import TrackingServiceProvider from '../../../../../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, isTrackingFieldEnabled, TRACKING_EVENTS } from '../../../../../../../tracking-service/tracking-values';
import { IOrganizationsDAO } from '../../../../../../../api/organizations.dao';
import { RegistrationConfigurationDAO } from '../../../../../../../api/implementations/registration-configuration.dao';
import { EnablementStatus } from '../../../../../../../constants/common.constants';
import { hasPermission } from '../../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../../api/roles.dao';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../../global/rich-text-editor/utils/transpiler';

export const notPublicPages = [CustomPageType.SingleMeeting, CustomPageType.Knovio, CustomPageType.UserShowcase, CustomPageType.BookAMeeting];
export class CustomPagesConfigurationVM {
    @mobx.observable
    private uploadEmbeddedHTMLZipStatus: boolean;
    private uploadEmbeddedHTMLZipStatuses: number[];
    @mobx.observable
    public disableConfigureButton: boolean = false;
    @mobx.observable
    changedElementId: number | string;
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }
    private initialEntity;

    public async initialize(): Promise<void[]> {
        if (hasPermission(Permission.PASSPORT_TAB)) {
            try {
                return Promise.all([this.initCustomPageTypes, this.initCustomPages].map(async fn => await fn()));
            } catch (e) {
                CustomPagesConfigurationVM.handleApiException(e);
            }
        }
    }

    private setInitialEntity(entity) {
        this.initialEntity = entity;
    }

    @mobx.action
    public async init() {
        this.jobsMonitor.registerHandler(JobType.UploadEmbeddedHTMLZip, this.handleUploadEmbeddedHTMLZip);
    }

    public get customPageTypes(): CustomPageType[] {
        const types = [...this.pageTypes];
        return types.sort((a, b) => {
            if (a === CustomPageType.BookAMeeting) return 1;
            if (b === CustomPageType.BookAMeeting) return -1;
            return 0;
        });
    }

    public getCustomPages(): CustomPageEntity[] {
        return this.pages.get();
    }

    public getCustomPageByType(type: CustomPageType): CustomPageEntity {
        return CustomPageMapper.toEntity(CustomPageEntityConfig.getConfigByType(type));
    }

    public entityToVm(entity: CustomPageEntity): CustomPageFormViewModel {
        return CustomPageMapper.toVM(entity, this.accountId, this.eventId, this.configureVm(entity));
    }

    @mobx.action
    public createCustomPage = async (): Promise<void> => {
        const entity = this.getCustomPageByType(this.customPageTypes[0]);
        const _props = await this.fetchDataForRestrictFilter();
        const meetingPresenterGroups = await this.fetchMeetingPresenterGroups();
        const vm = this.entityToVm(entity);
        this.normalizePresenterGroupsOnLoad(vm);

        this.customPageModal.openCreateCustomPage({
            pageVm: vm,
            title: 'Custom Page',
            types: this.customPageTypes,
            handlers: {
                onAdd: { handler: async () => this.onAddCustomPage(vm), shouldClose: true },
            },
            ..._props,
            meetingPresenterGroups: meetingPresenterGroups,
        });
    };

    public cleanup = () => {
        this.jobsMonitor.removeHandler(JobType.UploadEmbeddedHTMLZip, this.handleUploadEmbeddedHTMLZip);
    };

    private handleUploadEmbeddedHTMLZip = (
        job: IJobDescriptionDTO<IUploadThemeArchiveJobDetails, IUploadCustomPageZipJobResult>
    ) => {
        if (this.uploadEmbeddedHTMLZipStatuses.indexOf(job.jobId) === -1) {
            this.handleUploadEmbeddedHTMLZipJobStatusChange(job);
        }
    };

    @mobx.action
    private handleUploadEmbeddedHTMLZipJobStatusChange = async (
        job: IJobDescriptionDTO<IUploadThemeArchiveJobDetails, IUploadCustomPageZipJobResult>
    ) => {
        if (job.jobStatus === JobStatus.InProgress) {
            this.disableConfigureButton = false;
            this.uploadEmbeddedHTMLZipStatus = true;
        } else {
            if (this.uploadEmbeddedHTMLZipStatuses.indexOf(job.jobId) === -1) {
                this.uploadEmbeddedHTMLZipStatus = false;
                this.uploadEmbeddedHTMLZipStatuses.push(job.jobId);
                this.disableConfigureButton = false;
            }
        }
    };

    @action.bound
    public async editCustomPage(id: number): Promise<void> {
        if (this.fetching[id]) return;
        this.fetching[id] = true;
        const _props = await this.fetchDataForRestrictFilter();
        const meetingPresenterGroups = await this.fetchMeetingPresenterGroups();
        this.fetching[id] = false;
        const entity = this.pages.getById(id);
        const vm = this.entityToVm(entity);
        this.normalizePresenterGroupsOnLoad(vm);

        this.setInitialEntity(entity);

        this.customPageModal.openEditCustomPage({
            pageVm: vm,
            title: `Custom Page: ${entity.getProps().label}`,
            types: [entity.getType()],
            handlers: {
                onApply: { handler: async () => this.onUpdateCustomPage(vm) },
                onApplyAndClose: { handler: async () => this.onUpdateCustomPage(vm), shouldClose: true },
                onDelete: { handler: async () => this.onDeleteCustomPage(vm), shouldClose: true },
            },
            ..._props,
            meetingPresenterGroups: meetingPresenterGroups,
        });
    }

    @action.bound
    public async onDeleteCustomPage(pageVm: CustomPageFormViewModel): Promise<void> {
        this.pages.removeById(CustomPageMapper.vmToEntity(pageVm).getId());
        return await this.saveCurrentConfiguration();
    }
    @action.bound
    public async onUpdateCustomPage(pageVm: CustomPageFormViewModel): Promise<void> {
        pageVm.isValueChanged(LABEL)
        || pageVm.isValueChanged(PATH)
        || pageVm.isValueChanged(ENABLED)
        || pageVm.isValueChanged(PAGE_LINK_ENABLED)
        || pageVm.isValueChanged(LINK_DESCRIPTION)
            ? (this.disableConfigureButton = false)
            : (this.disableConfigureButton = true);
        this.changedElementId = pageVm.getId();

        const entity = CustomPageMapper.vmToEntity(pageVm);

        this.pages.update(entity);
        this.setInitialEntity(entity);
        this.trackEditCustomPage(entity);
        return await this.saveCurrentConfiguration();
    }
    @action.bound
    public async onAddCustomPage(pageVm: CustomPageFormViewModel): Promise<void> {
        this.changedElementId = '';
        this.disableConfigureButton = true;

        const entity = CustomPageMapper.vmToEntity(pageVm);
        this.pages.add(entity);

        this.trackNewCustomPage(entity);
        return await this.saveCurrentConfiguration();
    }

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly onSuccess: () => void,
        @IoC.Inject private readonly customPagesDAO: ICustomPagesDAO = IoC.Container.get(ICustomPagesDAO),
        @IoC.Inject private readonly jobsMonitor: IJobsMonitor = IoC.Container.get(IJobsMonitor),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly organizationsDAO: IOrganizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly registrationConfigurationDAO: RegistrationConfigurationDAO = IoC.Container.get(RegistrationConfigurationDAO),
    ) {
        this.uploadEmbeddedHTMLZipStatus = false;
        this.uploadEmbeddedHTMLZipStatuses = [];
    }

    private readonly customPageModal: PassportPageSubWindowService = PassportPageSubWindowService.init();

    @observable
    private readonly pageTypes: Set<CustomPageType> = new Set();
    @observable
    private readonly pages: CustomPagesCollection = new CustomPagesCollection();
    private fetching: Record<number, boolean> = {};

    private get eventParams(): { accountId: number; eventId: number } {
        return { accountId: this.accountId, eventId: this.eventId };
    }
    private getPayload(): ICustomPageConfigurationDTO {
        return {
            pages: this.getCustomPages().map(CustomPageMapper.toDTO),
        };
    }

    private configureVm(entity: CustomPageEntity): FormVmItemConfigType[] {
        const pathChecker = this.getEntityExistedPathChecker(entity);
        return pagePathValidatorComposer(pathChecker);
    }

    private getEntityExistedPathChecker(entity: CustomPageEntity): (path: string) => boolean {
        return (path: string) => {
            const page = this.pages.getByPagePath(path);
            return page ? !entity.equalsTo(page) && page.isPagePathEqual(path) : false;
        };
    }

    @action.bound
    public async initCustomPages(): Promise<void> {
        try {
            const { pages } = await this.customPagesDAO.getCustomPagesConfiguration(this.eventParams);
            const pagesList: CustomPageConfigurationType[] = pages.map(page => {
                if (page.type === CustomPageType.RichText) {
                    page.richText = transpileInlineStylesToClasses(page.richText);
                }
                return page;
            });
            this.resetPages(pagesList);
        } catch (e) {
            CustomPagesConfigurationVM.handleApiException(e);
        }
    }

    @action.bound
    private resetPages(pages: CustomPageConfigurationType[]): void {
        this.pages.reset();
        if (Array.isArray(pages)) {
            pages.forEach(page => this.pages.add(CustomPageMapper.toEntity(page)));
        }
    }

    @action.bound
    private async initCustomPageTypes(): Promise<void> {
        try {
            const { types } = await this.customPagesDAO.getCustomPageTypes(this.eventParams);
            if (Array.isArray(types)) {
                this.pageTypes.clear();
                types.forEach(type => this.pageTypes.add(type));
            }
        } catch (e) {
            CustomPagesConfigurationVM.handleApiException(e);
        }
    }

    @action.bound
    private async saveCurrentConfiguration(): Promise<void> {
        try {
            const payload = this.getPayload();
            payload.pages = payload.pages.map(page => {
                if (page.type === CustomPageType.RichText) {
                    page.richText = transpileClassesToInlineStyles(page.richText);
                }
                return page;
            });

            const { pages } = await this.customPagesDAO.updateCustomPagesConfiguration(
                this.eventParams,
                payload
            );

            this.resetPages(pages.map(page => {
                if (page.type === CustomPageType.RichText) {
                    page.richText = transpileInlineStylesToClasses(page.richText);
                }
                return page;
            }));
            this.onSuccess();
        } catch (e) {
            CustomPagesConfigurationVM.handleApiException(e);
        }
    }

    private getTrackingToggleStatus = (value: boolean): string => value ? EnablementStatus.DISABLED_UPPERCASE : EnablementStatus.DISABLED_UPPERCASE;

    private getGeneralTrackingParams = (entity: CustomPageEntity) => {
        const shouldShowPublicToggle = !notPublicPages.includes(entity.getType());
        return ({
            'Page Type': entity.getType(),
            'Page (after sign in) enabled': this.getTrackingToggleStatus(entity.getProps().enabled),
            ...( shouldShowPublicToggle && {'Page (public) enabled after sign in':  this.getTrackingToggleStatus(entity.getProps().publicEnabled)}),
            'Show link on Welcome page': this.getTrackingToggleStatus(entity.getProps().pageLinkEnabled),
        });
    };

    private trackNewCustomPage(entity: CustomPageEntity) {
        this.trackingService.get().track(TRACKING_EVENTS.NEW_CUSTOM_PAGE,
            {
                ...this.getGeneralTrackingParams(entity),
                'Description': convertBoolTypeForMixPanel(!!entity.getProps().linkDescription),
                'Restrict Access to Page': isTrackingFieldEnabled(entity.getProps().restrictAccessEnabled)
            }
        );
    }

    private trackEditCustomPage(entity: CustomPageEntity) {
        const isDescriptionChanged = entity.getProps().linkDescription !== this.initialEntity.getProps().linkDescription;
        this.trackingService.get().track(TRACKING_EVENTS.EDIT_CUSTOM_PAGE, {
            ...this.getGeneralTrackingParams(entity),
            'Description': convertBoolTypeForMixPanel(isDescriptionChanged),
            'Restrict Access to Page': isTrackingFieldEnabled(entity.getProps().restrictAccessEnabled)
        });
    }


    private async fetchDataForRestrictFilter(): Promise<IRestrictFilterProps> {
        const response = await Promise.all([
            this.fetchOrganizationTypes(),
            this.fetchCustomFieldsOptions()
        ]);
        return {
            organizationTypes: response[0],
            customFieldsOptionsList: response[1]
        };
    }


    private fetchOrganizationTypes = async (query?: string): Promise<string[]> => {
        try {
            const params = {
                size: 100,
                page: 0,
                query: query ? query.trim() : '',
                ...this.eventParams,
            };
            const response = await this.organizationsDAO.getOrganizationTypes(params);
            return response.content;
        } catch (error) {
            CustomPagesConfigurationVM.handleApiException(error);
        }
    };

    private fetchCustomFieldsOptions = async (): Promise<CustomFieldsOptionsListDTO[]> => {
        try {
            const response = await this.registrationConfigurationDAO.getCustomFieldsOptionsList(
                this.eventParams.eventId,
                this.eventParams.accountId,
            );
            return response.fields;
        } catch (error) {
            CustomPagesConfigurationVM.handleApiException(error);
        }
    };

    private fetchMeetingPresenterGroups = async (): Promise<IMeetingPresenterGroupDTO[]> => {
        try {
            const response = await this.customPagesDAO.getMeetingPresenterGroups(this.eventParams);
            return response;
        } catch (error) {
            CustomPagesConfigurationVM.handleApiException(error);
        }
    };

    private normalizePresenterGroupsOnLoad = (vm: CustomPageFormViewModel) => {
        const isEnabled = vm.getFieldByKey(PRESENTER_GROUPS_ENABLED).value === true;
        const selectedIds = (vm.getFieldByKey(PRESENTER_GROUPS_FILTERS).value as string[]) || [];
        const presenterGroups = (vm.getFieldByKey(PRESENTER_GROUPS).value as IMeetingPresenterGroupDTO[]) || [];

        const hasSelectedIds = selectedIds.length > 0;
        const hasPresenterGroups = presenterGroups.length > 0;

        if (isEnabled && hasSelectedIds && !hasPresenterGroups) {
            vm.setMeetingPresenterGroupsFiltersValid(false);
            vm.setValueTouched(PRESENTER_GROUPS_ENABLED);
        }
    };
}
